import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { FuelReportFilters, MonthOnMonthFilters, FilterOptionsResponse, FuelReportsService } from '../services/fuelReportsService';
import { useQuery } from '@tanstack/react-query';

interface FuelReportsContextType {
  // Filters
  filters: FuelReportFilters;
  monthOnMonthFilters: MonthOnMonthFilters;
  setFilters: (filters: Partial<FuelReportFilters>) => void;
  setMonthOnMonthFilters: (filters: Partial<MonthOnMonthFilters>) => void;
  resetFilters: () => void;
  
  // Filter options
  filterOptions: FilterOptionsResponse['data'] | null;
  isLoadingFilterOptions: boolean;
  
  // Date filter presets
  dateFilterPresets: { label: string; value: string }[];
  
  // Utility functions
  buildQueryParams: (customFilters?: Partial<FuelReportFilters>) => string;
  isValidDateRange: (startDate?: string, endDate?: string) => boolean;
}

const defaultFilters: FuelReportFilters = {
  dateFilter: 'lastMonth',
  startDate: '',
  endDate: '',
  vehicleModels: '',
  vehicleTypes: '',
  vehicleAge: '',
  districts: '',
  zones: '',
  regions: '',
  staffTypes: '',
  vehicleNumbers: '',
};

const defaultMonthOnMonthFilters: MonthOnMonthFilters = {
  monthsCount: 6,
  vehicleModels: '',
  vehicleTypes: '',
  vehicleAge: '',
  districts: '',
  zones: '',
  regions: '',
  staffTypes: '',
  vehicleNumbers: '',
};

const dateFilterPresets = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Last 2 Months', value: 'last2Months' },
  { label: 'Last 3 Months', value: 'last3Months' },
  { label: 'Last 6 Months', value: 'last6Months' },
  { label: 'Last 12 Months', value: 'last12Months' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Last Year', value: 'lastYear' },
  { label: 'Custom Range', value: 'custom' },
];

const FuelReportsContext = createContext<FuelReportsContextType | undefined>(undefined);

interface FuelReportsProviderProps {
  children: ReactNode;
  /** Merged into defaults for this page (e.g. vehicle report default month `2025-01`). */
  initialFilters?: Partial<FuelReportFilters>;
}

export const FuelReportsProvider: React.FC<FuelReportsProviderProps> = ({
  children,
  initialFilters,
}) => {
  const snapshotRef = useRef<FuelReportFilters | null>(null);
  if (snapshotRef.current === null) {
    snapshotRef.current = { ...defaultFilters, ...initialFilters };
  }

  const [filters, setFiltersState] = useState<FuelReportFilters>(
    snapshotRef.current
  );
  const [monthOnMonthFilters, setMonthOnMonthFiltersState] = useState<MonthOnMonthFilters>(defaultMonthOnMonthFilters);

  // Fetch filter options
  const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useQuery({
    queryKey: ['fuel-reports-filter-options'],
    queryFn: FuelReportsService.getFilterOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const setFilters = (newFilters: Partial<FuelReportFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  };

  const setMonthOnMonthFilters = (newFilters: Partial<MonthOnMonthFilters>) => {
    setMonthOnMonthFiltersState(prev => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    setFiltersState({ ...snapshotRef.current! });
    setMonthOnMonthFiltersState(defaultMonthOnMonthFilters);
  };

  const buildQueryParams = (customFilters?: Partial<FuelReportFilters>): string => {
    const activeFilters = { ...filters, ...customFilters };
    const params = new URLSearchParams();
    
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value);
      }
    });
    
    return params.toString();
  };

  const isValidDateRange = (startDate?: string, endDate?: string): boolean => {
    if (!startDate || !endDate) return false;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 28; // Minimum 28 days requirement
  };

  const value: FuelReportsContextType = {
    filters,
    monthOnMonthFilters,
    setFilters,
    setMonthOnMonthFilters,
    resetFilters,
    filterOptions: filterOptionsData?.data || null,
    isLoadingFilterOptions,
    dateFilterPresets,
    buildQueryParams,
    isValidDateRange,
  };

  return (
    <FuelReportsContext.Provider value={value}>
      {children}
    </FuelReportsContext.Provider>
  );
};

export const useFuelReports = (): FuelReportsContextType => {
  const context = useContext(FuelReportsContext);
  if (!context) {
    throw new Error('useFuelReports must be used within a FuelReportsProvider');
  }
  return context;
};
